import React, { useState, useEffect } from "react";
import { StyleSheet, View, TouchableOpacity, Linking, StyleProp, ViewStyle } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import Constants from "expo-constants";

import { ThemedText } from "@/components/ThemedText";
import { ThemedView } from "@/components/ThemedView";
import { CustomAlert } from "@/components/ThemedAlert";
import { useThemeColor } from "@/hooks/useThemeColor";
import { useAuth } from "@components/auth/useAuth";
import { useFirebase } from "@/components/firebase/useFirebase";
import { AppVersion, ProductVersion, ApiVersion } from "@/versions";
import { getAppConfig } from "@/services/globals";
import { getPrivacyPolicyUrl, getAboutUrl } from "@/services/environment-urls";
import { getServerStatus } from "@/services/firebase/status-functions";

/**
 * Props for the PlatformDetails component.
 */
export interface IPlatformDetailsProps {
  /** Optional authenticated user override; defaults to active user from useAuth(). */
  user?: any;
  /** Optional client environment string override (e.g. "LOCAL", "DEV", "PROD"). */
  clientEnv?: string;
  /** Optional server environment string override. */
  serverEnv?: string;
  /** Whether to show the Privacy Policy section. Default is true. */
  showPrivacyPolicy?: boolean;
  /** Whether to show the About Memories section. Default is true. */
  showAbout?: boolean;
  /** Custom container style override. */
  style?: StyleProp<ViewStyle>;
}

/**
 * Reusable component displaying platform details, environment telemetry,
 * product versions, and links to Privacy Policy and About pages.
 */
export function PlatformDetails({
  user: userProp,
  clientEnv: clientEnvProp,
  serverEnv: serverEnvProp,
  showPrivacyPolicy = true,
  showAbout = true,
  style,
}: IPlatformDetailsProps): React.JSX.Element {
  const auth = useAuth();
  const services = useFirebase();

  const user = userProp || auth.user;

  const cardBgColor = useThemeColor({}, "card");
  const borderColor = useThemeColor({}, "border");
  const tintColor = useThemeColor({}, "tint");

  // Server Environment state
  const [internalServerEnv, setInternalServerEnv] = useState<string>("CONNECTING...");

  useEffect(() => {
    if (serverEnvProp) return;
    if (!services) return;

    getServerStatus(services)
      .then((status) => {
        setInternalServerEnv(status.environment ? status.environment.toUpperCase() : "UNKNOWN");
      })
      .catch((err) => {
        console.error("Failed to fetch server status in PlatformDetails:", err);
        setInternalServerEnv("UNAVAILABLE");
      });
  }, [services, serverEnvProp]);

  const effectiveServerEnv = serverEnvProp || internalServerEnv;

  // Client Environment resolver
  const resolvedClientEnv = (() => {
    if (clientEnvProp) return clientEnvProp;
    try {
      return getAppConfig().environ.getName().toUpperCase();
    } catch {
      const extraEnv = Constants.expoConfig?.extra?.env;
      return extraEnv ? String(extraEnv).toUpperCase() : "LOCAL";
    }
  })();

  const privacyPolicyUrl = getPrivacyPolicyUrl(resolvedClientEnv);
  const aboutUrl = getAboutUrl(resolvedClientEnv);

  const handleOpenPrivacyPolicy = async () => {
    try {
      await Linking.openURL(privacyPolicyUrl);
    } catch (err) {
      console.error("Failed to open Privacy Policy URL:", err);
      CustomAlert.alert("Privacy Policy", `Please visit: ${privacyPolicyUrl}`);
    }
  };

  const handleOpenAbout = async () => {
    try {
      await Linking.openURL(aboutUrl);
    } catch (err) {
      console.error("Failed to open About URL:", err);
      CustomAlert.alert("About Memories", `Please visit: ${aboutUrl}`);
    }
  };

  return (
    <ThemedView
      style={[
        styles.platformCard,
        { backgroundColor: cardBgColor, borderColor },
        style,
      ]}
      testID="platform-details-container"
    >
      <View style={styles.sectionHeaderRow}>
        <Ionicons name="hardware-chip-outline" size={20} color={tintColor} />
        <ThemedText type="subtitle" style={styles.platformSectionTitle}>
          Platform
        </ThemedText>
      </View>

      <View style={styles.platformInfoGrid}>
        {user && (
          <>
            <View style={styles.platformRow}>
              <ThemedText style={styles.platformLabel}>Active User</ThemedText>
              <ThemedText style={styles.platformValue}>
                {user.email || user.displayName || user.userId}
              </ThemedText>
            </View>
            <View style={styles.platformRow}>
              <ThemedText style={styles.platformLabel}>User ID</ThemedText>
              <ThemedText
                style={[styles.platformValue, styles.monoText]}
                numberOfLines={1}
                ellipsizeMode="middle"
              >
                {user.userId}
              </ThemedText>
            </View>
          </>
        )}
        <View style={styles.platformRow}>
          <ThemedText style={styles.platformLabel}>Product Version</ThemedText>
          <ThemedText style={styles.platformValue}>v{ProductVersion}</ThemedText>
        </View>
        <View style={styles.platformRow}>
          <ThemedText style={styles.platformLabel}>App Version</ThemedText>
          <ThemedText style={styles.platformValue}>v{AppVersion}</ThemedText>
        </View>
        <View style={styles.platformRow}>
          <ThemedText style={styles.platformLabel}>Client Environment</ThemedText>
          <ThemedText style={styles.platformValue}>{resolvedClientEnv}</ThemedText>
        </View>
        <View style={styles.platformRow}>
          <ThemedText style={styles.platformLabel}>Server Environment</ThemedText>
          <ThemedText style={styles.platformValue}>{effectiveServerEnv}</ThemedText>
        </View>
        <View style={styles.platformRow}>
          <ThemedText style={styles.platformLabel}>API Version</ThemedText>
          <ThemedText style={styles.platformValue}>{ApiVersion}</ThemedText>
        </View>
      </View>

      {showPrivacyPolicy && (
        <>
          <View style={[styles.platformDivider, { backgroundColor: borderColor }]} />
          <View style={styles.privacySection}>
            <View style={styles.privacyHeaderInline}>
              <Ionicons name="shield-checkmark-outline" size={18} color={tintColor} />
              <ThemedText style={styles.privacyHeading}>Privacy Policy</ThemedText>
            </View>
            <ThemedText style={styles.privacySubtext}>
              Review how your personal data, rights of deletion, prohibited content rules, and platform protections are handled.
            </ThemedText>
            <View style={styles.privacyActionRow}>
              <TouchableOpacity
                onPress={handleOpenPrivacyPolicy}
                accessibilityRole="link"
                accessibilityLabel="Open Privacy Policy URL"
                style={styles.privacyUrlLink}
              >
                <ThemedText style={[styles.privacyUrlText, { color: tintColor }]}>
                  {privacyPolicyUrl}
                </ThemedText>
                <Ionicons name="open-outline" size={14} color={tintColor} />
              </TouchableOpacity>

              <TouchableOpacity
                style={[styles.viewPolicyBtn, { borderColor: tintColor }]}
                onPress={handleOpenPrivacyPolicy}
                accessibilityRole="button"
                accessibilityLabel="View Privacy Policy"
              >
                <ThemedText style={[styles.viewPolicyBtnText, { color: tintColor }]}>
                  View Policy
                </ThemedText>
              </TouchableOpacity>
            </View>
          </View>
        </>
      )}

      {showAbout && (
        <>
          <View style={[styles.platformDivider, { backgroundColor: borderColor }]} />
          <View style={styles.privacySection}>
            <View style={styles.privacyHeaderInline}>
              <Ionicons name="information-circle-outline" size={18} color={tintColor} />
              <ThemedText style={styles.privacyHeading}>About Memories</ThemedText>
            </View>
            <ThemedText style={styles.privacySubtext}>
              Learn what Memories is, who the developers are, and why this sanctuary for life's meaningful moments was created.
            </ThemedText>
            <View style={styles.privacyActionRow}>
              <TouchableOpacity
                onPress={handleOpenAbout}
                accessibilityRole="link"
                accessibilityLabel="Open About URL"
                style={styles.privacyUrlLink}
              >
                <ThemedText style={[styles.privacyUrlText, { color: tintColor }]}>
                  {aboutUrl}
                </ThemedText>
                <Ionicons name="open-outline" size={14} color={tintColor} />
              </TouchableOpacity>

              <TouchableOpacity
                style={[styles.viewPolicyBtn, { borderColor: tintColor }]}
                onPress={handleOpenAbout}
                accessibilityRole="button"
                accessibilityLabel="View About Page"
              >
                <ThemedText style={[styles.viewPolicyBtnText, { color: tintColor }]}>
                  View About
                </ThemedText>
              </TouchableOpacity>
            </View>
          </View>
        </>
      )}
    </ThemedView>
  );
}

export default PlatformDetails;

const styles = StyleSheet.create({
  platformCard: {
    borderRadius: 0,
    padding: 12,
    marginTop: 12,
    borderWidth: 1,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.04,
    shadowRadius: 4,
    elevation: 1,
  },
  sectionHeaderRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    marginBottom: 12,
  },
  platformSectionTitle: {
    fontSize: 16,
    fontWeight: "700",
  },
  platformInfoGrid: {
    gap: 8,
  },
  platformRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingVertical: 3,
  },
  platformLabel: {
    fontSize: 12,
    opacity: 0.75,
  },
  platformValue: {
    fontSize: 12,
    fontWeight: "600",
  },
  monoText: {
    fontFamily: "monospace",
    maxWidth: "60%",
  },
  platformDivider: {
    height: StyleSheet.hairlineWidth,
    marginVertical: 12,
  },
  privacySection: {
    gap: 8,
  },
  privacyHeaderInline: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },
  privacyHeading: {
    fontSize: 14,
    fontWeight: "600",
  },
  privacySubtext: {
    fontSize: 12,
    opacity: 0.75,
    lineHeight: 16,
  },
  privacyActionRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: 8,
    marginTop: 2,
  },
  privacyUrlLink: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    flexShrink: 1,
  },
  privacyUrlText: {
    fontSize: 12,
    fontWeight: "500",
    textDecorationLine: "underline",
  },
  viewPolicyBtn: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 0,
    borderWidth: 1,
    alignItems: "center",
    justifyContent: "center",
  },
  viewPolicyBtnText: {
    fontSize: 12,
    fontWeight: "600",
  },
});
